import { useEffect, useRef } from 'react';

import type { DatadogRumConfig } from './config';
import { HOST_CONSENT_CHANGE_EVENT } from './consent';
import {
  DATADOG_RUM_SCRIPT_URL,
  hasAnalyticsConsent,
  initializeRum,
  setRumTrackingConsent,
  syncRumSurface,
  type DatadogRum,
} from './rum';

let loader: Promise<DatadogRum | undefined> | undefined;

export function loadDatadogRum(): Promise<DatadogRum | undefined> {
  if (window.DD_RUM) {
    return Promise.resolve(window.DD_RUM);
  }
  if (loader) {
    return loader;
  }

  loader = new Promise((resolve) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = DATADOG_RUM_SCRIPT_URL;
    script.onload = () => {
      const rum = window.DD_RUM;
      if (!rum) {
        loader = undefined;
      }
      resolve(rum);
    };
    script.onerror = () => {
      loader = undefined;
      resolve(undefined);
    };
    document.head.append(script);
  });
  return loader;
}

export function resetRumLoaderForTests(): void {
  loader = undefined;
}

export async function getRumConfig(): Promise<DatadogRumConfig | undefined> {
  try {
    const response = await fetch('/api/observability-config', {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!response.ok || response.status === 204) {
      return undefined;
    }
    return (await response.json()) as DatadogRumConfig;
  } catch {
    return undefined;
  }
}

export type RumInitializationDependencies = {
  getRumConfig: () => Promise<DatadogRumConfig | undefined>;
  loadDatadogRum: () => Promise<DatadogRum | undefined>;
  hasAnalyticsConsent: () => boolean;
  getRum: () => DatadogRum | undefined;
  initializeRum: (config: DatadogRumConfig | undefined, rum: DatadogRum | undefined, pathname?: string) => boolean;
  setRumTrackingConsent: (rum: DatadogRum | undefined, consent: 'granted' | 'not-granted') => void;
};

function revokeRumConsent(dependencies: RumInitializationDependencies, rum?: DatadogRum): void {
  dependencies.setRumTrackingConsent(rum ?? dependencies.getRum(), 'not-granted');
}

export async function initializeRumWithConsent(
  dependencies: RumInitializationDependencies,
  canInitialize: () => boolean = () => true,
  pathname?: string | (() => string | undefined),
): Promise<void> {
  if (!dependencies.hasAnalyticsConsent()) {
    revokeRumConsent(dependencies);
    return;
  }

  const config = await dependencies.getRumConfig();
  if (!dependencies.hasAnalyticsConsent()) {
    revokeRumConsent(dependencies);
    return;
  }
  if (!config || !canInitialize()) {
    return;
  }

  const rum = await dependencies.loadDatadogRum();
  if (!dependencies.hasAnalyticsConsent()) {
    revokeRumConsent(dependencies, rum);
    return;
  }
  if (!canInitialize()) {
    return;
  }

  dependencies.initializeRum(config, rum, typeof pathname === 'function' ? pathname() : pathname);
}

export function RumInitializer(
  { pathname, getPathname }: { pathname?: string; getPathname?: () => string | undefined } = {},
) {
  const getPathnameRef = useRef(getPathname ?? (() => pathname));

  useEffect(() => {
    getPathnameRef.current = getPathname ?? (() => pathname);
  }, [getPathname, pathname]);

  useEffect(() => {
    let disposed = false;
    const initialize = () => {
      void initializeRumWithConsent({
        getRumConfig,
        loadDatadogRum,
        hasAnalyticsConsent,
        getRum: () => window.DD_RUM,
        initializeRum,
        setRumTrackingConsent,
      }, () => !disposed, () => getPathnameRef.current());
    };

    initialize();
    window.addEventListener(HOST_CONSENT_CHANGE_EVENT, initialize);
    return () => {
      disposed = true;
      window.removeEventListener(HOST_CONSENT_CHANGE_EVENT, initialize);
    };
  }, []);

  useEffect(() => {
    syncRumSurface(window.DD_RUM, pathname);
  }, [pathname]);

  return null;
}
